import { useCallback, useMemo, useState } from "react";

/**
 * «إخفاء» / «إظهار» — PLATE MARKS KEPT ON THIS DEVICE (T2, T7; D170).
 *
 * A retained «مدفوع» card dismissed with «إخفاء», and an aged-off READY plate
 * pinned back into its lane with «إظهار», are this SCREEN's business: nothing
 * is sent and no state is added on the server. Stored as plate ids under one
 * key per mark, wrapped like every storage read here — a private window or
 * blocked site data THROWS on access, and the board must still work (the mark
 * then lasts for the page's life). The list is capped, oldest first out: a
 * plate leaves the board within a business day, so its mark is soon dead.
 */

export const KITCHEN_HIDDEN_PAID_KEY = "triotyKitchenHiddenPaid";
export const KITCHEN_PINNED_READY_KEY = "triotyKitchenPinnedReady";
const MAX_MARKS = 300;

function read(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function write(key: string, ids: readonly string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // Unwritable storage: the mark holds for this page's life only.
  }
}

export function useKitchenDeviceMarks(key: string): {
  ids: ReadonlySet<string>;
  add: (plateIds: readonly string[]) => void;
  remove: (plateIds: readonly string[]) => void;
} {
  const [list, setList] = useState<string[]>(() => read(key));
  const add = useCallback(
    (plateIds: readonly string[]) =>
      setList((current) => {
        const next = [...current.filter((id) => !plateIds.includes(id)), ...plateIds].slice(-MAX_MARKS);
        write(key, next);
        return next;
      }),
    [key],
  );
  const remove = useCallback(
    (plateIds: readonly string[]) =>
      setList((current) => {
        const next = current.filter((id) => !plateIds.includes(id));
        write(key, next);
        return next;
      }),
    [key],
  );
  const ids = useMemo(() => new Set(list), [list]);
  return { ids, add, remove };
}
